import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_ITEM_ELIGIBILITY_CONFIG } from '../../config/claim-item-eligibility.config';
import { ClaimService } from '../../services';

type Result = Claim.ClaimItem.Eligibility.Model;
type Finding = Claim.ClaimItem.Eligibility.Finding;
const { Outcome, Step } = Claim.ClaimItem.Eligibility;

/**
 * A job's latest eligibility check, its history, and the button to run it again.
 * Checks on its own when there's no result yet or the claim changed since the last one.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-eligibility',
  templateUrl: './claim-item-eligibility.component.html',
  standalone: false,
})
export class ClaimItemEligibilityComponent implements OnChanges {
  constructor(
    private claimSvc: ClaimService,
    private toastSvc: ToastService
  ) {}

  @Input() claim!: Claim.Model;
  @Input() item!: Claim.ClaimItem.Model;
  @Input() canCheck = false;

  /** One line for the decision dialog instead of the full card. */
  @Input() compact = false;

  /** Cents being approved; null when nothing is being approved. */
  @Input() authorizedAmount: number | null = null;

  /** Cents the job's latest decision approved; null unless it was approved. */
  @Input() approvedAmount: number | null = null;

  /** The latest result once loaded, and again after each check. */
  @Output() latestChange = new EventEmitter<Result | null>();

  readonly config = CLAIM_ITEM_ELIGIBILITY_CONFIG;

  results: Result[] | null = null;
  loadFailed = false;
  checking = false;
  showEarlier = false;

  /** Earlier checks opened to show why they came out as they did, by version. */
  private expandedEarlier = new Set<number>();

  // The inputs an automatic check last ran for, so a failed one isn't retried in a loop.
  private autoCheckedFor: string | null = null;

  readonly themes: Record<Claim.ClaimItem.Eligibility.Outcome, ThemeColor> = {
    [Outcome.ELIGIBLE]: 'success',
    [Outcome.LIMITED]: 'warn',
    [Outcome.EXCLUDED]: 'danger',
    [Outcome.INELIGIBLE]: 'danger',
    [Outcome.INSUFFICIENT_INFORMATION]: 'default',
  };

  get latest(): Result | null {
    return this.results?.[0] ?? null;
  }

  get earlier(): Result[] {
    return this.results?.slice(1) ?? [];
  }

  get changed() {
    return this.latest ? Claim.ClaimItem.Eligibility.changedInputs(this.latest, this.current()) : [];
  }

  get problem() {
    return this.results
      ? Claim.ClaimItem.Eligibility.approvalProblem(this.latest, this.current(), this.authorizedAmount)
      : null;
  }

  /** On a Limited result, what was approved against what's left. */
  get approvedLine() {
    const limits = this.latest?.limits;
    if (this.latest?.outcome !== Outcome.LIMITED || !limits || this.approvedAmount === null) return null;

    const within = this.approvedAmount <= limits.remainingLimit;
    const text = within ? this.config.approved.within : this.config.approved.over;
    return {
      within,
      text: text(
        this.formatAmount(this.approvedAmount),
        this.formatAmount(limits.requestedAmount),
        this.formatAmount(limits.remainingLimit)
      ),
    };
  }

  /** The reason behind a blocking result, shown under the warning. */
  get blockingFinding(): Finding | null {
    const problem = this.problem;
    return problem?.code === 'NOT_ELIGIBLE' ? problem.finding : null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['item'] && changes['item'].previousValue?.id !== this.item?.id) void this.load();
    else if (changes['item'] || changes['claim'] || changes['canCheck']) void this.autoCheck();
  }

  async load() {
    this.results = null;
    this.loadFailed = false;
    this.expandedEarlier.clear();
    if (!this.item) return;

    const itemId = this.item.id;
    const response = await this.claimSvc.listItemEligibility(this.item.claimId, itemId).catch(() => null);
    if (this.item?.id !== itemId) return;

    if (!response) {
      this.loadFailed = true;
      return;
    }

    // The server sends newest first.
    this.results = response.items;
    this.latestChange.emit(this.latest);
    await this.autoCheck();
  }

  /** Runs a check without a click when there's none yet or the claim changed since the last one. */
  async autoCheck() {
    if (!this.canCheck || !this.results || this.checking || !this.item) return;
    if (this.claim?.status !== Claim.Status.OPEN) return;
    if (this.latest && !this.changed.length) return;

    const key = `${this.item.id}:${JSON.stringify(this.current())}`;
    if (this.autoCheckedFor === key) return;
    this.autoCheckedFor = key;

    await this.check({ quiet: true });
  }

  /** `quiet` skips the toasts, for checks the page runs on its own. */
  async check(options: { quiet?: boolean } = {}) {
    if (this.checking || !this.item) return;

    // The page can move to another job while this one is being checked.
    const { id: itemId, claimId } = this.item;
    const stillOnJob = () => this.item?.id === itemId;

    this.checking = true;
    try {
      const result = await this.claimSvc.checkItemEligibility(claimId, itemId).catch((response: HttpErrorResponse) => {
        if (options.quiet) return null;
        const message = response?.error?.message;
        this.toastSvc.show({
          type: 'error',
          title: this.config.toasts.failedTitle,
          text: Array.isArray(message) ? message.join(' ') : message,
        });
        return null;
      });
      if (!result || !stillOnJob()) return;

      // Reload so the new check comes back with who ran it.
      const history = await this.claimSvc.listItemEligibility(claimId, itemId).catch(() => null);
      if (!stillOnJob()) return;
      this.results = history?.items.length ? history.items : [result, ...(this.results ?? [])];
      if (!options.quiet) {
        this.toastSvc.show({ type: 'success', ...this.config.toasts.checked(this.config.results[result.outcome]) });
      }
      this.latestChange.emit(this.latest);
    } finally {
      this.checking = false;
      // The job that's showing now was skipped while this check ran.
      if (!stillOnJob()) void this.autoCheck();
    }
  }

  isExpanded(result: Result) {
    return this.expandedEarlier.has(result.version);
  }

  toggleEarlier(result: Result) {
    if (!this.expandedEarlier.delete(result.version)) this.expandedEarlier.add(result.version);
  }

  approvalText(problem: Claim.ClaimItem.Eligibility.ApprovalProblem) {
    switch (problem.code) {
      case 'NOT_EVALUATED':
        return this.config.approval.NOT_EVALUATED;
      case 'OUT_OF_DATE':
        return this.config.approval.OUT_OF_DATE;
      case 'NOT_ELIGIBLE':
        return this.config.approval.NOT_ELIGIBLE(this.config.states[problem.outcome]);
      case 'OVER_LIMIT':
        return this.config.approval.OVER_LIMIT(this.formatAmount(problem.limit));
    }
  }

  /** Limits are in cents; other values are shown as stored. */
  formatValue(finding: Finding, value: unknown) {
    if (value === null || value === undefined || value === '') return '-';
    if (finding.step === Step.LIMITS && typeof value === 'number') return this.formatAmount(value);
    if (finding.step === Step.USAGE && typeof value === 'number') return this.formatNumber(value);
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  operatorText(operator: string | null | undefined) {
    return operator ?? '';
  }

  // A reading at loss below the one at sale can't be measured, so no negative "used".
  measuredUse(usage: Claim.ClaimItem.Eligibility.UsageCheck) {
    return usage.used !== null && usage.used >= 0 ? usage.used : null;
  }

  usageStatus(usage: Claim.ClaimItem.Eligibility.UsageCheck) {
    if (usage.passed === null) return this.config.usage.missing;
    return usage.passed ? this.config.usage.passed : this.config.usage.failed;
  }

  hasValues(finding: Finding) {
    return finding.expected !== undefined || finding.actual !== undefined;
  }

  formatAmount(amount: number | null | undefined) {
    return amount === null || amount === undefined ? '-' : '$' + (amount / 100).toFixed(2);
  }

  formatNumber(value: number | null) {
    return value === null ? '-' : value.toLocaleString('en-US');
  }

  private current() {
    return currentEligibilityInputs(this.claim, this.item);
  }
}

/** The claim and job values a check depends on, as the page has them now. */
export function currentEligibilityInputs(
  claim: Claim.Model | null | undefined,
  item: Claim.ClaimItem.Model | null | undefined
): Claim.ClaimItem.Eligibility.CurrentInputs {
  return {
    lossDate: claim?.lossDate ?? null,
    claimDate: claim?.claimDate ?? null,
    coverageId: item?.coverageId ?? null,
    usageAtLoss: claim?.usageAtLoss ?? null,
    submissionId: claim?.propertySetSubmissionId ?? claim?.warranty?.propertySetSubmissionId ?? null,
  };
}
